'use client'

import { useEffect, useMemo, useState } from 'react'
import { ComboBoxView } from './combo-box'
import { OptionType } from 'types/OptionType'

const DEFAULT_DESELECT_ALL_OPTIONS: OptionType[] = [{ name: `All`, id: `0`, value: `` }]

export type ComboMultiSelectProps = {
  name: string
  className?: string
  inputClassName?: string
  optionsContainerClassName?: string
  options: OptionType[]
  placeholder?: string
  values?: OptionType[]
  loading?: boolean
  icon?: React.ReactElement
  isFilter?: boolean
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  disabled?: boolean
  checkboxSide?: `left` | `right`
  customButton?: React.ReactNode | ((isOpen: boolean) => React.ReactNode)
  addText?: string
  avoidCollisions?: boolean
  isOptionsGrouped?: boolean
  onChange: (options: OptionType[]) => void
  onAddNewValue?: (val: string) => void
}

export default function ComboMultiSelect({
  name,
  className,
  inputClassName,
  optionsContainerClassName,
  options,
  placeholder,
  values,
  loading = false,
  enableDeselectAllOption = false,
  deselectAllOptions = DEFAULT_DESELECT_ALL_OPTIONS,
  disabled,
  icon,
  isFilter = false,
  checkboxSide,
  customButton,
  addText,
  avoidCollisions,
  isOptionsGrouped,
  onChange,
  onAddNewValue,
}: ComboMultiSelectProps) {
  const [query, setQuery] = useState(``)
  const [selected, setSelected] = useState<OptionType[]>(
    values !== undefined ? values : enableDeselectAllOption ? [deselectAllOptions[0]] : [],
  )
  const filteredOptions = useMemo(
    () =>
      query === ``
        ? options.concat(enableDeselectAllOption ? [...deselectAllOptions] : [])
        : options
            .filter((option) => option.name.toLowerCase().includes(query.toLowerCase()))
            .concat(enableDeselectAllOption ? [...deselectAllOptions] : []),
    [query, options, enableDeselectAllOption, deselectAllOptions],
  )

  const onChangeHandler = (newOptions: OptionType[]) => {
    const difference = newOptions.filter((option) => !selected.includes(option))

    // Parent-child deselection: selecting a child deselects its parent
    if (
      difference.some((option) =>
        selected.some(
          (selectedOption) =>
            selectedOption.children?.includes(option) ||
            selectedOption.children?.some((childOption) => childOption.children?.includes(option)),
        ),
      )
    ) {
      const parentIds = selected
        .filter(
          (selectedOption) =>
            selectedOption.children?.find((childOption) => difference.map((opt) => opt.id).includes(childOption.id)) ||
            selectedOption.children?.some((childOption) =>
              childOption.children?.find((grandchildOption) =>
                difference.map((opt) => opt.id).includes(grandchildOption.id),
              ),
            ),
        )
        .map((option) => option.id)
      const result = newOptions.filter((option) => !parentIds.includes(option.id))
      setSelected(result)
      onChange(result)
      return
    }

    // Selecting a parent deselects its children
    if (
      difference.some((diffOption) =>
        diffOption.children?.some(
          (childOption) =>
            selected.map((opt) => opt.id).includes(childOption.id) ||
            childOption.children?.some((grandchildOption) =>
              selected.map((opt) => opt.id).includes(grandchildOption.id),
            ),
        ),
      )
    ) {
      const ids = selected
        .filter(
          (selectedOption) =>
            difference.some((diffOption) => diffOption.id === selectedOption.meta?.parentId) ||
            difference.some((diffOption) =>
              diffOption.children?.some((childOption) => childOption.id === selectedOption.meta?.parentId),
            ),
        )
        .map((option) => option.id)
      const result = newOptions.filter((option) => !ids.includes(option.id))
      setSelected(result)
      onChange(result)
      return
    }

    // Deselect-all handling
    if (difference.some((option) => deselectAllOptions.some((deselOption) => option.value === deselOption.value))) {
      setSelected(difference)
      onChange(difference)
    } else {
      const optsWithoutDeselectAll = newOptions.filter(
        (option) => !deselectAllOptions.some((opt) => opt.value === option.value),
      )
      setSelected(optsWithoutDeselectAll)
      onChange(optsWithoutDeselectAll)
    }
  }

  useEffect(() => {
    setSelected(values !== undefined ? values : enableDeselectAllOption ? [deselectAllOptions[0]] : [])
  }, [values])

  return (
    <ComboBoxView
      name={name}
      className={className}
      inputClassName={inputClassName}
      optionsContainerClassName={optionsContainerClassName}
      options={filteredOptions}
      placeholder={placeholder}
      selected={selected}
      loading={loading}
      enableDeselectAllOption={enableDeselectAllOption}
      deselectAllOptions={deselectAllOptions}
      disabled={disabled}
      multiple
      customIcon={icon}
      chevronClassName={isFilter ? `pr-3.5` : undefined}
      checkboxSide={checkboxSide}
      isFilter={isFilter}
      customButton={customButton}
      addText={addText}
      avoidCollisions={avoidCollisions}
      isOptionsGrouped={isOptionsGrouped}
      onChangeMultiple={onChangeHandler}
      onClearSearch={() => setQuery(``)}
      onSearchValueChange={(text) => setQuery(text)}
      onAddNewValue={onAddNewValue}
    />
  )
}
